
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { AnalyticsData } from "@/types/analytics";

import { ProductivityScore } from "@/components/analytics/ProductivityScore";
import { EisenhowerMatrix } from "@/components/analytics/EisenhowerMatrix";
import { AnalyticsGraphs } from "@/components/analytics/AnalyticsGraphs";
import { ExportAnalytics } from "@/components/analytics/ExportAnalytics";



export function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        console.log("Starting analytics fetch...");
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError("Please sign in to view analytics");
          return;
        }

        // First, fetch boards
        const { data: boards, error: boardsError } = await supabase
          .from('boards')
          .select('*')
          .or(`owner_id.eq.${user.id}, user_id.eq.${user.id} `);

        if (boardsError) {
          console.error('Error fetching boards:', boardsError);
          throw new Error('Failed to fetch boards');
        }

        if (!boards || boards.length === 0) {
          setData({
            totalTasks: 0,
            completedTasks: 0,
            completionRate: 0,
            averageCompletionTime: 0,
            tasksByPriority: { critical: 0, important: 0, normal: 0 },
            tasksByDay: {},
            projectHealth: { totalProjects: 0, activeProjects: 0, completedProjects: 0 },
            taskAging: [],
            productivity: {
              daily: { score: 0, tasksCompleted: 0, avgCompletionTime: "0h", urgentResponseTime: "0h" },
              weekly: { score: 0, trend: "stable", improvement: 0 },
              monthly: { score: 0, trend: "stable", improvement: 0 }
            },
            optimization: [],
            eisenhowerMatrix: {
              urgent: { important: 0, notImportant: 0 },
              notUrgent: { important: 0, notImportant: 0 }
            },
            completionTrends: {
              monthly: {},
              quarterly: {},
              yearly: {},
              seasonalPatterns: { spring: 0, summer: 0, fall: 0, winter: 0 }
            }
          });
          return;
        }

        // Then, fetch columns for all boards
        const { data: columns, error: columnsError } = await supabase
          .from('columns')
          .select('*')
          .in('board_id', boards.map(b => b.id));

        if (columnsError) {
          console.error('Error fetching columns:', columnsError);
          throw new Error('Failed to fetch columns');
        }

        // Finally, fetch cards for all columns
        const { data: cards, error: cardsError } = await supabase
          .from('cards')
          .select('*')
          .in('column_id', columns.map(c => c.id));

        if (cardsError) {
          console.error('Error fetching cards:', cardsError);
          throw new Error('Failed to fetch cards');
        }

        // Initialize analytics data
        const analyticsData: AnalyticsData = {
          totalTasks: 0,
          completedTasks: 0,
          completionRate: 0,
          averageCompletionTime: 0,
          tasksByPriority: { critical: 0, important: 0, normal: 0 },
          tasksByDay: {},
          projectHealth: {
            totalProjects: boards.length,
            activeProjects: boards.filter(b =>
              columns.some(c => c.board_id === b.id &&
                cards.some(card => card.column_id === c.id))
            ).length,
            completedProjects: boards.filter(b =>
              columns.filter(c => c.board_id === b.id)
                .every(c => c.title.toLowerCase().includes('done') ||
                  !cards.some(card => card.column_id === c.id))
            ).length
          },
          taskAging: [],
          productivity: {
            daily: { score: 0, tasksCompleted: 0, avgCompletionTime: "0h", urgentResponseTime: "0h" },
            weekly: { score: 0, trend: "stable", improvement: 0 },
            monthly: { score: 0, trend: "stable", improvement: 0 }
          },
          optimization: [],
          eisenhowerMatrix: {
            urgent: { important: 0, notImportant: 0 },
            notUrgent: { important: 0, notImportant: 0 }
          },
          completionTrends: {
            monthly: {},
            quarterly: {},
            yearly: {},
            seasonalPatterns: { spring: 0, summer: 0, fall: 0, winter: 0 }
          }
        };

        // Process all cards
        let totalCompletionTime = 0;
        let completedCards = 0;

        cards.forEach(card => {
          // Total tasks
          analyticsData.totalTasks++;

          const column = columns.find(c => c.id === card.column_id);
          const isDoneColumn = column?.title.toLowerCase().includes('done');

          // Completed tasks
          if (isDoneColumn) {
            analyticsData.completedTasks++;
            if (card.completed_at) {
              const completionTime = new Date(card.completed_at).getTime() - new Date(card.created_at).getTime();
              totalCompletionTime += completionTime;
              completedCards++;
            }
          }

          // Task priorities
          if (card.priority) {
            analyticsData.tasksByPriority[card.priority.toLowerCase() as keyof typeof analyticsData.tasksByPriority]++;
          }

          // Tasks by day
          const day = new Date(card.created_at).toISOString().split('T')[0];
          analyticsData.tasksByDay[day] = (analyticsData.tasksByDay[day] || 0) + 1;

          // Eisenhower Matrix
          const isUrgent = card.priority === 'critical';
          const isImportant = card.priority === 'important' || card.priority === 'critical';

          if (isUrgent) {
            if (isImportant) {
              analyticsData.eisenhowerMatrix.urgent.important++;
            } else {
              analyticsData.eisenhowerMatrix.urgent.notImportant++;
            }
          } else {
            if (isImportant) {
              analyticsData.eisenhowerMatrix.notUrgent.important++;
            } else {
              analyticsData.eisenhowerMatrix.notUrgent.notImportant++;
            }
          }
        });

        // Calculate completion rate and average completion time
        analyticsData.completionRate = analyticsData.totalTasks > 0
          ? (analyticsData.completedTasks / analyticsData.totalTasks) * 100
          : 0;

        analyticsData.averageCompletionTime = completedCards > 0
          ? totalCompletionTime / completedCards / (1000 * 60 * 60) // Convert to hours
          : 0;

        // Calculate productivity scores
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];

        analyticsData.productivity.daily = {
          score: Math.min(100, (analyticsData.tasksByDay[todayStr] || 0) * 10),
          tasksCompleted: analyticsData.tasksByDay[todayStr] || 0,
          avgCompletionTime: `${analyticsData.averageCompletionTime.toFixed(1)} h`,
          urgentResponseTime: "2h" // This would need actual urgent task response time tracking
        };

        // Weekly and monthly trends
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);

        const weeklyTasks = Object.entries(analyticsData.tasksByDay)
          .filter(([date]) => new Date(date) >= lastWeek)
          .reduce((sum, [, count]) => sum + count, 0);

        const monthlyTasks = Object.entries(analyticsData.tasksByDay)
          .filter(([date]) => new Date(date) >= lastMonth)
          .reduce((sum, [, count]) => sum + count, 0);

        analyticsData.productivity.weekly = {
          score: Math.min(100, weeklyTasks * 2),
          trend: weeklyTasks > 20 ? 'up' : weeklyTasks > 10 ? 'stable' : 'down',
          improvement: weeklyTasks > 20 ? 15 : weeklyTasks > 10 ? 0 : -10
        };

        analyticsData.productivity.monthly = {
          score: Math.min(100, monthlyTasks),
          trend: monthlyTasks > 80 ? 'up' : monthlyTasks > 40 ? 'stable' : 'down',
          improvement: monthlyTasks > 80 ? 20 : monthlyTasks > 40 ? 0 : -15
        };

        console.log("Setting analytics data:", analyticsData);
        setData(analyticsData);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(err instanceof Error ? err.message : "Failed to fetch analytics data");
      } finally {
        setLoading(false);
      }
    }

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
        {data && (
          <ExportAnalytics
            data={{
              productivityScore: data.productivity.daily.score,
              totalTasks: data.totalTasks,
              completedTasks: data.completedTasks,
              eisenhowerMatrix: {
                urgent_important: data.eisenhowerMatrix.urgent.important,
                not_urgent_important: data.eisenhowerMatrix.notUrgent.important,
                urgent_not_important: data.eisenhowerMatrix.urgent.notImportant,
                not_urgent_not_important: data.eisenhowerMatrix.notUrgent.notImportant
              },
              tasksByPriority: data.tasksByPriority
            }}
          />
        )}
      </div>

      {/* Task Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Task Overview</h2>
          <div className="space-y-2">
            <p>Total Tasks: {data?.totalTasks || 0}</p>
            <p>Completed Tasks: {data?.completedTasks || 0}</p>
            <p>Completion Rate: {(data?.completionRate || 0).toFixed(1)}%</p>
            <p>Avg. Completion Time: {(data?.averageCompletionTime || 0).toFixed(1)}h</p>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Project Health</h2>
          <div className="space-y-2">
            <p>Total Projects: {data?.projectHealth.totalProjects || 0}</p>
            <p>Active Projects: {data?.projectHealth.activeProjects || 0}</p>
            <p>Completed Projects: {data?.projectHealth.completedProjects || 0}</p>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Task Priorities</h2>
          <div className="space-y-2">
            <p>Critical: {data?.tasksByPriority.critical || 0}</p>
            <p>Important: {data?.tasksByPriority.important || 0}</p>
            <p>Normal: {data?.tasksByPriority.normal || 0}</p>
          </div>
        </div>
      </div>

      {/* Analytics Graphs */}
      {data && (
        <AnalyticsGraphs
          tasksByDay={data.tasksByDay}
          tasksByPriority={data.tasksByPriority}
          completionRate={data.completionRate}
        />
      )}

      {/* Productivity Score */}
      {data && (
        <div className="mb-8">
          <ProductivityScore data={data.productivity} />
        </div>
      )}

      {/* Eisenhower Matrix */}
      {data && (
        <div className="mb-8">
          <EisenhowerMatrix data={data.eisenhowerMatrix} />
        </div>
      )}
    </div>
  );
} 